import {
  Inject,
  Injectable,
  OnInit
} from '@angular/core';
import { AppStore } from '@iv/store/app.store';
import * as Redux from 'redux';
import { AppState } from '@iv/store/app.reducer';
import { PermissionTypes } from '@iv/store/permissions/permissions.models';
import { getPermissions } from '@iv/store/permissions/permissions.reducer';
import {
  forkJoin,
  Observable,
  Subject
} from 'rxjs';
import { CommonService } from './common.service';
import { getHttpParams } from '@iv/shared/utils';
import { IStockFutureResponse } from '../interfaces/stock-future-list.interface';
import {
  finalize,
  map,
  switchMap,
  tap
} from 'rxjs/operators';
import { HttpResponse } from '@angular/common/http';
import {
  Future,
  Instrument
} from '@iv/model/instrument';
import * as InstrumentActions from '@iv/store/instruments/instrument.actions';
import * as FuturesAction from '@iv/store/futures/futures.actions';
import { FavouritesService } from 'app/favourites/services/favourites.service';
import { FuturesMonitorService } from 'app/futures-monitor/services/futures-monitor.service';
import { Group } from '@iv/model/group';
import { cleanObjectFromNullProperties } from '@iv/shared/helpers/common.helper';
import * as FuturesMonitorActions from 'app/futures-monitor/redux/futures-monitor.actions';
import * as FavoritesActions from 'app/favourites/redux/favorites.actions';
import {
  Tab,
  TabTypeEnum
} from '@iv/model/tabs.model';
import * as ColumnsActions from '@iv/store/columns/columns.actions';
import * as TabsActions from '@iv/store/tabs/tabs.actions';
import {
  IUserSettingsResponse,
  IUserSubscription
} from 'app/core/interfaces/user-settings.interface';
import { AdvancedSearchHttpService } from 'app/advanced-search/services/advanced-search-http.service';

type keyStocks = 'stocks';
type keyFutures = 'futures';
type keyFinancialInstruments = keyStocks | keyFutures;

interface IStockAndFutureHttpResponse<T1, T2> {
  stocks?: IStockFutureResponse<T1>;
  futures?: IStockFutureResponse<T2>;
}

interface IStockAndFuturesMappedData<T1, T2> {
  stocks?: T1;
  futures?: T2;
}

type keyFavoriteGroups = 'favoriteServiceGroups';
type keyFuturesGroups = 'futuresServiceGroups';
type keyGroups = keyFavoriteGroups | keyFuturesGroups;

interface IFavoritesAndFuturesGroupsResponse<T> {
  favoriteServiceGroups?: T;
  futuresServiceGroups?: T;
}

type keyFavoriteDefaultGroups = 'favoriteServiceGroups';
type keyFuturesDefaultGroups = 'futuresServiceGroups';
type keyDefaultGroups = keyFavoriteDefaultGroups | keyFuturesDefaultGroups;

interface IFavoritesAndFuturesDefaultGroupsResponse<T> {
  favoriteServiceDefaultGroups?: T;
  futuresServiceDefaultGroups?: T;
}

@Injectable()
/*
 * @class AppStateService
 * Предназначен для формирования сторы приложения при первичной загрузке.
 */
export class AppStateService implements OnInit {

  constructor(
    @Inject(AppStore) private store: Redux.Store<AppState>,
    private readonly commonService: CommonService,
    private readonly advancedSearchHttpService: AdvancedSearchHttpService,
    private readonly favoritesService: FavouritesService,
    private readonly futuresMonitorService: FuturesMonitorService,
  ) {
    this.permissions = getPermissions(this.store.getState());
  }

  public initialDataLoaded = new Subject();

  public get haveFuturesAccess(): boolean {
    return !!this.permissions.find(permission => {
      return permission === PermissionTypes.EOD_FUTURES || permission === PermissionTypes.RT_FUTURES;
    });
  }

  public get haveStocksAccess(): boolean {
    return !!this.permissions.find(permission => {
      return permission === PermissionTypes.DELAYED_STOCKS || permission === PermissionTypes.RT_STOCKS;
    });
  }

  private SPY_ID = 9327; // Дефолтный сток
  private FUT_ID = 215; // Дефолтный фьюч
  private permissions: PermissionTypes[];

  private static formListEntities(entitiesList: Instrument[] | Future[], isFuture: boolean): Instrument[] | Future[] {
    const entities: Instrument[] | Future[] = [];
    for (let i = 0; i < entitiesList.length; i++) {
      const entity = isFuture ? new Future() : new Instrument();
      Object.assign(entity, entitiesList[i]);
      entities.push(entity);
    }

    return entities;
  }

  public ngOnInit(): void {
    this.initialDataLoaded.next(false);

    forkJoin({
      financialInstruments: this.initListEntities(),
      monitorsGroups: this.initGroups(),
      monitorsDefaultGroups: this.initDefaultGroups(),
      userState: this.initUserSettingsState()
    })
      .pipe(
        tap((data: {
          financialInstruments: IStockAndFuturesMappedData<Instrument[], Future[]>,
          monitorsGroups: IFavoritesAndFuturesGroupsResponse<Group[]>,
          monitorsDefaultGroups: IFavoritesAndFuturesDefaultGroupsResponse<Group[]>,
          userState: IUserSettingsResponse
        }) => {
          this.fillFinancialInstrumentStore(data.financialInstruments);
          this.fillGroupsStore(data.monitorsGroups);
          this.fillDefaultGroupsStore(data.monitorsDefaultGroups);
        }),
        switchMap((data: { userState: IUserSettingsResponse }) => this.fillUserSettingsStore(data.userState)),
        finalize(() => this.initialDataLoaded.next(true))
      )
      .subscribe();
  }

  public groupsHasChanges(groups: any, newGroups: any) {
    if (groups.length !== newGroups.length) {
      return true;
    }
    for (let i = 0; i < groups.length; i++) {
      if (groups[i].id !== newGroups[i].id || groups[i].name !== newGroups[i].name) {
        return true;
      }
    }
    return false;
  }

  public columnsConfigHasChanges(config: any, newConfig: any) {
    const oldValues = Object.values(config).sort().join('');
    const newValues = Object.values(newConfig).sort().join('');
    return !(oldValues === newValues);
  }

  public sortGroups(group1, group2) {
    if (group1.name.toLowerCase() < group2.name.toLowerCase()) {
      return -1;
    }
    if (group1.name.toLowerCase() > group2.name.toLowerCase()) {
      return 1;
    }
    return 0;
  }

  public configInitTab(isStock: boolean = this.haveStocksAccess, isFut: boolean = this.haveFuturesAccess): { [name: string]: number } {
    let result: { [name: string]: number } = {};

    if (isStock) {
      result = {instrument: this.SPY_ID, service: 1};
    }
    if (isFut) {
      result = {futuresMonitor: 2, future: this.FUT_ID};
    }
    if (isStock && isFut) {
      result = {instrument: this.SPY_ID, service: 1,  futuresMonitor: 2, future: this.FUT_ID};
    }
    if (this.commonService.userSubscriptions
      .find((sub: IUserSubscription) => (sub.serviceId === 1007 || sub.serviceId === 1008) && sub.subscriptionCancelTypeId === 1)) {
      result = {...result, chartService: 3};
    }

    return result;
  }

  private initListEntities(): Observable<IStockAndFuturesMappedData<Instrument[], Future[]>> {
    const defaultParams = getHttpParams({region: '0', matchingType: 'CONTAINS', sortField: 'SYMBOL'});
    const listObservables = {
      stocks: this.haveStocksAccess ? this.advancedSearchHttpService.getListEntities(defaultParams, false) : null,
      futures: this.haveFuturesAccess ? this.advancedSearchHttpService.getListEntities(defaultParams, true) : null
    };
    return forkJoin(cleanObjectFromNullProperties(listObservables))
      .pipe(map((res: IStockAndFutureHttpResponse<Instrument[], Future[]>) => {
        const stockAndFuturesMappedData: IStockAndFuturesMappedData<Instrument[], Future[]> = {};
        Object.keys(res).forEach((key: keyFinancialInstruments) => {
          stockAndFuturesMappedData[key] = res[key].page;
        });

        return stockAndFuturesMappedData;
      }));
  }

  private fillFinancialInstrumentStore(entitiesList: IStockAndFuturesMappedData<Instrument[], Future[]>): void {
    const {stocks, futures} = entitiesList;
    if (stocks) this.store.dispatch(InstrumentActions.addInstrument(AppStateService.formListEntities(stocks, false)));
    if (futures) this.store.dispatch(FuturesAction.addFuture(AppStateService.formListEntities(futures, true)));
  }

  private initGroups(): Observable<IFavoritesAndFuturesGroupsResponse<Group[]>> {
    const listGroups = {
      favoriteServiceGroups: this.haveStocksAccess ? this.favoritesService.getAll() : null,
      futuresServiceGroups: this.haveFuturesAccess ? this.futuresMonitorService.getAll() : null
    };

    return forkJoin(cleanObjectFromNullProperties(listGroups))
      .pipe(map((res: IFavoritesAndFuturesGroupsResponse<HttpResponse<Group[]>>) => {
        const groupsMappedData: { favoriteServiceGroups?: Group[], futuresServiceGroups?: Group[] } = {};
        Object.keys(res).forEach((key: keyGroups) => {
          groupsMappedData[key] = res[key].body;
        });

        return groupsMappedData;
      }));
  }

  private fillGroupsStore(userGroups: IFavoritesAndFuturesGroupsResponse<Group[]>): void {
    // TODO: Когда избавимся от дублированного монитора, убрать ненужное
    if (this.haveFuturesAccess) {
      userGroups.futuresServiceGroups.sort(this.sortGroups);
      userGroups.futuresServiceGroups.forEach((group: Group) => {
        this.store.dispatch(FuturesMonitorActions.addGroup(group));
      });
    }

    if (this.haveStocksAccess) {
      userGroups.favoriteServiceGroups.sort(this.sortGroups);
      userGroups.favoriteServiceGroups.forEach((group: Group) => {
        this.store.dispatch(FavoritesActions.addGroup(group));
      });
    }
  }

  private initDefaultGroups(): Observable<IFavoritesAndFuturesDefaultGroupsResponse<Group[]>> {
    const listDefaultGroups = {
      favoriteServiceDefaultGroups: this.haveStocksAccess ? this.favoritesService.getDefaultGroups() : null,
      futuresServiceDefaultGroups: this.haveFuturesAccess ? this.futuresMonitorService.getDefaultGroups() : null
    };

    return forkJoin(cleanObjectFromNullProperties(listDefaultGroups))
      .pipe(map((res: IFavoritesAndFuturesDefaultGroupsResponse<HttpResponse<Group[]>>) => {
        const groupsMappedData: { favoriteServiceDefaultGroups?: Group[], futuresServiceDefaultGroups?: Group[] } = {};
        Object.keys(res).forEach((key: keyDefaultGroups) => {
          groupsMappedData[key] = res[key].body;
        });

        return groupsMappedData;
      }));
  }

  private fillDefaultGroupsStore(defaultGroups: IFavoritesAndFuturesDefaultGroupsResponse<Group[]>): void {
    // TODO: Когда избавимся от дублированного монитора, убрать ненужное
    if (this.haveStocksAccess) {
      defaultGroups.favoriteServiceDefaultGroups.forEach((group: Group) => {
        group.isDefault = true;
        // "84" Старая логика, которую перес сюда, не знаю зачем это надо
        if (group.id !== 84) this.store.dispatch(FavoritesActions.addDefaultGroup(group));
      });
    }

    if (this.haveFuturesAccess) {
      defaultGroups.futuresServiceDefaultGroups.forEach((group: Group) => {
        group.isDefault = true;
        if (group.id !== 84) this.store.dispatch(FuturesMonitorActions.addDefaultGroup(group));
      });
    }
  }

  private initUserSettingsState(): Observable<IUserSettingsResponse> {
    return this.commonService.getState().pipe(map((state: HttpResponse<IUserSettingsResponse>) => state.body));
  }

  private fillUserSettingsStore(userSettings: IUserSettingsResponse): Promise<void> {
    this.store.dispatch(ColumnsActions.loadColumns(userSettings.data.columnConfigs));
    return this.filterUserTabs(userSettings.data.tabs)
      .then((tabs: Tab[]) => {
        this.store.dispatch(TabsActions.loadTabs(tabs));
      });
  }

  private filterUserTabs(tabs: Tab[]): Promise<Tab[]> {
    return new Promise<Tab[]>((resolve => {
      const filteredTabs: Tab[] = tabs.filter((tab: Tab) => {
        if (
          (this.commonService.userSubscriptions
              .find((sub: IUserSubscription) => (sub.serviceId === 1007 || sub.serviceId === 1008) && sub.subscriptionCancelTypeId === 1) &&
          tab.type === TabTypeEnum.CHART_SERVICE)
        ) {
          return tab;
        }

        if (this.haveStocksAccess && (tab.type === TabTypeEnum.STOCK_MONITOR || tab.type === TabTypeEnum.OPTION_CHAIN)) {
          return tab;
        }

        if (this.haveFuturesAccess && (tab.type === TabTypeEnum.FUTURES_OPTION_CHAIN || tab.type === TabTypeEnum.FUTURES_MONITOR)) {
          return tab;
        }
      });

      if (!filteredTabs.length) {
        console.warn('All tabs was deleted after change permissions');
        localStorage.setItem('permissions', this.permissions.join(';'));
      }

      resolve(filteredTabs);
    }));
  }
}
